import { useMemo, useRef } from "react";
import type { Hint } from "~/features/chat/types";
import type { TermMatch, TermMatcher } from "~/utils/termMatcher";

/**
 * Escapes special regex characters in a string.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Builds a single combined regex from all terms for efficient matching.
 * Terms are sorted by length (longest first) to ensure longer phrases
 * match before shorter substrings.
 */
function buildTermRegex(terms: Hint[]): RegExp | null {
  if (!terms.length) return null;

  const sorted = [...terms].sort((a, b) => b.term.length - a.term.length);
  const pattern = sorted.map((t) => escapeRegex(t.term)).join("|");
  return new RegExp(`\\b(${pattern})\\b`, "gi");
}

/**
 * Escapes HTML special characters for safe injection into markup.
 */
function escapeHtml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Escapes a string for use in an HTML attribute value.
 */
function escapeAttr(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Protected segments regex: code blocks, inline code, HTML tags, links */
const PROTECTED_RE =
  /(`{3,}[\s\S]*?`{3,}|`[^`]+`|<[^>]+>|\[[^\]]*\]\([^)]*\))/g;

/**
 * Splits content into protected and unprotected segments.
 * Protected segments (code, HTML, links) are never highlighted.
 */
function splitProtected(
  content: string
): Array<{ text: string; isProtected: boolean }> {
  const segments: { text: string; isProtected: boolean }[] = [];
  let lastIndex = 0;

  let match: RegExpExecArray | null;
  PROTECTED_RE.lastIndex = 0;
  while ((match = PROTECTED_RE.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        text: content.slice(lastIndex, match.index),
        isProtected: false,
      });
    }
    segments.push({ text: match[0], isProtected: true });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({ text: content.slice(lastIndex), isProtected: false });
  }

  return segments;
}

/**
 * Finds all regex matches in a text segment, returning position-based results.
 */
function findRegexMatches(text: string, regex: RegExp): TermMatch[] {
  const matches: TermMatch[] = [];
  regex.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m[0].length === 0) {
      regex.lastIndex++;
      continue;
    }
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      term: m[0].toLowerCase(),
    });
  }
  return matches;
}

/**
 * Merges two sorted match arrays, preferring AI terms over medications
 * when they overlap. Returns non-overlapping matches sorted by position.
 */
function mergeMatches(
  aiMatches: TermMatch[],
  medMatches: TermMatch[]
): TermMatch[] {
  const combined = [...aiMatches, ...medMatches];
  combined.sort((a, b) => a.start - b.start || b.end - a.end);

  // Mark AI matches with priority — when overlapping, AI wins
  const aiSet = new Set(aiMatches);
  const result: TermMatch[] = [];
  let lastEnd = 0;

  for (const m of combined) {
    if (m.start >= lastEnd) {
      result.push(m);
      lastEnd = m.end;
    } else if (aiSet.has(m) && m.start < lastEnd) {
      // AI match overlaps with a previously accepted med match — replace
      if (result.length > 0 && !aiSet.has(result[result.length - 1])) {
        result[result.length - 1] = m;
        lastEnd = m.end;
      }
    }
  }

  return result;
}

/**
 * Injects <mark> tags at specified positions in text.
 */
function injectMarks(text: string, matches: TermMatch[]): string {
  if (!matches.length) return text;

  const parts: string[] = [];
  let cursor = 0;

  for (const m of matches) {
    if (m.start > cursor) {
      parts.push(text.slice(cursor, m.start));
    }
    const original = text.slice(m.start, m.end);
    const key = escapeAttr(m.term);
    const safeText = escapeHtml(original);
    parts.push(
      `<mark class="term-highlight" data-term-key="${key}" tabindex="0" role="button">${safeText}</mark>`
    );
    cursor = m.end;
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts.join("");
}

/**
 * Highlights terms in content using both regex (AI terms) and
 * Aho-Corasick (medications) matchers.
 */
function highlightContent(
  content: string,
  regex: RegExp | null,
  matcher: TermMatcher | null
): string {
  const segments = splitProtected(content);

  const processed = segments.map((segment) => {
    if (segment.isProtected) return segment.text;

    const aiMatches = regex ? findRegexMatches(segment.text, regex) : [];
    const medMatches = matcher ? matcher.search(segment.text) : [];

    if (!aiMatches.length && !medMatches.length) return segment.text;

    const merged = mergeMatches(aiMatches, medMatches);
    return injectMarks(segment.text, merged);
  });

  return processed.join("");
}

/**
 * Hook that injects `<mark>` tags around matched terms in markdown content.
 *
 * Supports two term sources:
 * - AI-extracted terms (small set, ~10-50): matched via single combined regex
 * - Medications dictionary (large set, ~9500): matched via Aho-Corasick O(n)
 *
 * AI term definitions take priority when overlapping with medications.
 *
 * The `<mark>` tags include a `data-term-key` attribute (lowercased term)
 * for lookup by the shared TermDefinitionDialog component via event delegation.
 */
export function useTermHighlighting(
  content: string,
  terms: Hint[],
  medicationMatcher?: TermMatcher | null
): string {
  const prevTermsRef = useRef<Hint[]>([]);
  const prevRegexRef = useRef<RegExp | null>(null);

  const regex = useMemo(() => {
    const prev = prevTermsRef.current;
    if (
      prev === terms ||
      (prev.length === terms.length &&
        prev.every((t, i) => t.term === terms[i].term))
    ) {
      return prevRegexRef.current;
    }
    prevTermsRef.current = terms;
    const built = buildTermRegex(terms);
    prevRegexRef.current = built;
    return built;
  }, [terms]);

  const matcher = medicationMatcher ?? null;

  return useMemo(() => {
    if (!regex && !matcher) return content;
    if (!content) return content;
    return highlightContent(content, regex, matcher);
  }, [content, regex, matcher]);
}
